// biome-ignore-all lint/plugin: pre-existing, outside the ADS color pairing in ENG-1520
import Link from 'next/link';
import { memo } from 'react';

import { type Board } from '@air/api/types';

import { usePublicShowcaseNavBoardNavigation } from '~/components/v2/components/showcase/hooks/usePublicShowcaseNavBoardNavigation';

export type PublicShowcaseNavBoardMenuItemProps = {
  board: Board;
  /** The page this board is listed under — `null` for Home/the root page. */
  pageId: string | null;
  /** The listing page's slug, or `null` for Home — builds the board's `?page={slug}` route. */
  slug: string | null;
};

const CLASS_NAME =
  'block w-full truncate rounded-4 px-2 py-1.5 text-left text-14 ads:text-secondary text-gray-11 no-underline transition-colors hover:ads:bg-hover-subtle hover:bg-gray-3 hover:ads:text-primary hover:text-gray-12 hover:no-underline focus-visible:ads:bg-hover-subtle focus-visible:bg-gray-3 focus-visible:ads:text-primary focus-visible:text-gray-12 focus-visible:outline-none';

/**
 * One board row inside a page's desktop nav dropdown. When the surface's navigation has URLs the
 * row is a shallow `next/link` to the board route (carrying `?page={slug}` for a sub-page) — the
 * same shallow navigation the nav's page links use. When it has none (no `href` — the collection
 * link editor's Edit tab), the row is a button that drills via the navigation contract instead.
 */
export const PublicShowcaseNavBoardMenuItem = memo(({ board, pageId, slug }: PublicShowcaseNavBoardMenuItemProps) => {
  const { href, handleSelect } = usePublicShowcaseNavBoardNavigation({ boardId: board.id, pageId, slug });

  if (!href) {
    return (
      <button
        className={CLASS_NAME}
        data-testid="PUBLIC_COLLECTION_NAV_BOARD_ITEM"
        onClick={handleSelect}
        type="button"
      >
        {board.title}
      </button>
    );
  }

  return (
    <Link className={CLASS_NAME} data-testid="PUBLIC_COLLECTION_NAV_BOARD_ITEM" href={href} shallow>
      {board.title}
    </Link>
  );
});

PublicShowcaseNavBoardMenuItem.displayName = 'PublicShowcaseNavBoardMenuItem';
